import { EventEmitter } from 'events';

import whisperService from './whisperService';

export interface VoiceCommand {
  id: string;
  command: string;
  confidence: number;
  timestamp: Date;
  processed: boolean;
}

export interface VoiceSettings {
  wakeWord: string;
  language: string;
  continuousListening: boolean;
  pushToTalk: boolean;
  voiceFeedback: boolean;
  confidenceThreshold: number;
  useWhisper: boolean;
}

export interface VoiceState {
  isListening: boolean;
  isProcessing: boolean;
  isSpeaking: boolean;
  isActive: boolean;
  wakeWordDetected: boolean;
  lastCommand?: VoiceCommand;
  error?: string;
}

class VoiceService extends EventEmitter {
  private settings: VoiceSettings = {
    wakeWord: 'hey legal',
    language: 'en-US',
    continuousListening: true,
    pushToTalk: false,
    voiceFeedback: true,
    confidenceThreshold: 0.7,
    useWhisper: true,
  };
  private state: VoiceState = {
    isActive: false,
    isListening: false,
    isProcessing: false,
    isSpeaking: false,
    wakeWordDetected: false,
    lastCommand: undefined,
  };
  private commandHistory: VoiceCommand[] = [];
  private isInitialized = false;
  private lastTranscription: string = '';

  constructor() {
    super();
    console.log('🔧 VoiceService constructor called');
    console.log('🔧 VoiceService initial settings:', {
      ...this.settings,
      useWhisper: this.settings.useWhisper,
    });

    // Initialize service - only MediaRecorder support required
    this.isInitialized = !!window.MediaRecorder;
    this.state.isActive = this.isInitialized;
    this.emit('stateChange', this.state);

    if (!this.isInitialized) {
      console.warn('⚠️ VoiceService: MediaRecorder not supported, voice features disabled');
    }
  }

  // --- Whisper-only transcription ---

  public async startListening(): Promise<void> {
    console.log('🔧 VoiceService.startListening() called');
    console.log('🔧 VoiceService: useWhisper setting:', this.settings.useWhisper);

    if (!this.isInitialized) {
      throw new Error('Voice service not initialized - MediaRecorder not supported');
    }

    return this.startListeningWithWhisper();
  }

  public async stopListening(): Promise<string | null> {
    console.log('🔧 VoiceService.stopListening() called');
    console.log('🔧 VoiceService: useWhisper setting:', this.settings.useWhisper);
    return this.stopListeningWithWhisper();
  }

  public async startListeningWithWhisper(): Promise<void> {
    console.log('🎤 VoiceService: startListeningWithWhisper() called');
    console.log('🎤 VoiceService: Current state:', this.state);

    if (this.state.isListening) {
      console.log('🎤 VoiceService: Already listening, returning early');
      return;
    }

    if (!this.settings.useWhisper) {
      console.log('🎤 VoiceService: Whisper is disabled in settings');
      throw new Error('Whisper is disabled in settings');
    }

    try {
      console.log('🎤 VoiceService: Checking Whisper availability...');
      const availability = await whisperService.checkAvailability();
      console.log('🎤 VoiceService: Whisper availability result:', availability);

      if (!availability.available) {
        console.error('🎤 VoiceService: Whisper not available:', availability.error);
        throw new Error(availability.error || 'Whisper not available');
      }

      console.log('🎤 VoiceService: Setting state to listening...');
      this.state.isListening = true;
      this.state.isProcessing = false;
      this.emit('stateChange', this.state);
      console.log('🎤 VoiceService: State updated, emitting stateChange');

      console.log('🎤 VoiceService: About to call whisperService.startRecording()');
      await whisperService.startRecording();
      console.log('🎤 VoiceService: whisperService.startRecording() completed successfully');

      this.emit('stateChange', this.state);
      console.log('🎤 VoiceService: Final stateChange emitted');
    } catch (error) {
      console.error('🎤 VoiceService: ❌ Error starting Whisper recording:', error);
      this.state.error =
        error instanceof Error ? error.message : 'Failed to start Whisper recording';
      this.state.isListening = false;
      this.emit('stateChange', this.state);
      this.emit('error', this.state.error);
      throw error;
    }
  }

  public async stopListeningWithWhisper(): Promise<string | null> {
    console.log('🎤 VoiceService: stopListeningWithWhisper() called');
    console.log('🎤 VoiceService: Current state:', this.state);

    if (!this.state.isListening) {
      console.log('🎤 VoiceService: Not currently listening, returning null');
      return null;
    }

    try {
      console.log('🎤 VoiceService: Setting state to processing...');
      this.state.isProcessing = true;
      this.emit('stateChange', this.state);
      console.log('🎤 VoiceService: State updated to processing');

      console.log('🎤 VoiceService: About to call whisperService.completeRecording()');
      const transcription = await whisperService.completeRecording();
      console.log('🎤 VoiceService: whisperService.completeRecording() result:', transcription);

      console.log('🎤 VoiceService: Setting state to not listening...');
      this.state.isListening = false;
      this.state.isProcessing = false;
      this.emit('stateChange', this.state);
      console.log('🎤 VoiceService: State updated to not listening');

      if (transcription && transcription.text) {
        console.log('🎤 VoiceService: ✅ Transcription received:', transcription.text);
        this.lastTranscription = transcription.text;
        return transcription.text;
      } else {
        console.log('🎤 VoiceService: ⚠️ No transcription received from Whisper');
        return null;
      }
    } catch (error) {
      console.error('🎤 VoiceService: ❌ Error with Whisper transcription:', error);
      this.state.error = error instanceof Error ? error.message : 'Whisper transcription failed';
      this.state.isListening = false;
      this.state.isProcessing = false;
      this.emit('stateChange', this.state);
      this.emit('error', this.state.error);
      return null;
    }
  }

  // --- Command processing ---

  private processCommand(transcript: string, confidence: number): void {
    console.log('🔄 Processing command:', transcript, 'Confidence:', confidence);
    if (confidence < this.settings.confidenceThreshold) {
      const errorMsg = "I didn't catch that. Could you repeat?";
      console.log('❌ Low confidence, rejecting command');
      // Placeholder: OpenAI TTS integration for feedback
      window.dispatchEvent(
        new CustomEvent('voice-error', {
          detail: { message: errorMsg, confidence, transcript },
        })
      );
      return;
    }
    this.state.isProcessing = true;
    this.emit('stateChange', this.state);
    const command: VoiceCommand = {
      id: Date.now().toString(),
      command: transcript,
      confidence,
      timestamp: new Date(),
      processed: false,
    };
    this.commandHistory.push(command);
    this.state.lastCommand = command;
    this.state.wakeWordDetected = false;
    console.log('📝 Command added to history:', command);
    this.emit('command', command);
    window.dispatchEvent(
      new CustomEvent('voice-command-parsed', {
        detail: { command, transcript, confidence },
      })
    );
    // Placeholder: OpenAI TTS integration for feedback
  }

  // --- Settings and state ---

  public updateSettings(newSettings: Partial<VoiceSettings>): void {
    console.log('🔧 VoiceService.updateSettings() called with:', newSettings);
    console.log('🔧 VoiceService: Previous useWhisper:', this.settings.useWhisper);
    this.settings = { ...this.settings, ...newSettings };
    console.log('🔧 VoiceService: New useWhisper:', this.settings.useWhisper);
    this.emit('settingsChanged', this.settings);
  }

  public getSettings(): VoiceSettings {
    console.log('🔧 VoiceService.getSettings() called, current settings:', {
      ...this.settings,
      useWhisper: this.settings.useWhisper,
    });
    return { ...this.settings };
  }

  public getState(): VoiceState {
    return { ...this.state };
  }

  public getCommandHistory(): VoiceCommand[] {
    return [...this.commandHistory];
  }

  public clearCommandHistory(): void {
    this.commandHistory = [];
    this.emit('historyCleared');
  }

  public isSupported(): boolean {
    // Only check for MediaRecorder support - no Web Speech API
    return !!window.MediaRecorder && this.settings.useWhisper;
  }

  public destroy(): void {
    this.stopListening();
    this.removeAllListeners();
  }
}

// Create singleton instance
const voiceService = new VoiceService();

export default voiceService;
